// Image Message Component
// Following existing patterns from MessageItem

import React, { useState, useCallback } from 'react';
import { ImageMessageProps } from '../../../types/messaging';
import {
  ImageMessageContainer,
  ImagePreview,
  ImageFullscreen,
  ImageOverlay,
  ImageActions,
  ImageActionButton,
  ImageInfo,
  ImageName,
  ImageSize,
  LoadingSpinner
} from '../../../styles/Messaging/MessageTypes.styles';

export const ImageMessage: React.FC<ImageMessageProps> = ({
  message,
  isOwn,
  onAction
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleImageClick = useCallback(() => {
    setIsFullscreen(true);
  }, []);

  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleDownload = useCallback(() => {
    if (message.attachments?.[0]?.url) {
      const link = document.createElement('a');
      link.href = message.attachments[0].url;
      link.download = message.attachments[0].filename || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [message.attachments]);

  const handleShare = useCallback(() => {
    if (navigator.share && message.attachments?.[0]?.url) {
      navigator.share({
        title: 'Shared Image',
        url: message.attachments[0].url
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(message.attachments?.[0]?.url || '').catch(console.error);
    }
  }, [message.attachments]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const imageAttachment = message.attachments?.[0];
  const imageUrl = imageAttachment?.url;
  const imageName = imageAttachment?.filename || 'image';
  const imageSize = imageAttachment?.size || 0;

  if (!imageUrl) {
    return (
      <ImageMessageContainer $isOwn={isOwn}>
        <div style={{ padding: '1rem', textAlign: 'center', color: '#6c757d' }}>
          Image not available
        </div>
      </ImageMessageContainer>
    );
  }

  return (
    <>
      <ImageMessageContainer $isOwn={isOwn}>
        <ImagePreview onClick={handleImageClick}>
          {isLoading && (
            <LoadingSpinner>
              <div className="spinner"></div>
            </LoadingSpinner>
          )}
          
          {hasError ? (
            <div style={{ 
              padding: '2rem', 
              textAlign: 'center', 
              color: '#6c757d',
              background: '#f8f9fa',
              borderRadius: '8px'
            }}>
              Failed to load image
            </div>
          ) : (
            <img
              src={imageUrl}
              alt={imageName}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: isLoading ? 'none' : 'block'
              }}
            />
          )}
          
          <ImageActions>
            <ImageActionButton onClick={handleDownload} title="Download">
              ⬇️
            </ImageActionButton>
            <ImageActionButton onClick={handleShare} title="Share">
              🔗
            </ImageActionButton>
          </ImageActions>
        </ImagePreview>
        
        <ImageInfo>
          <ImageName>{imageName}</ImageName>
          <ImageSize>{formatFileSize(imageSize)}</ImageSize>
        </ImageInfo>
        
        {message.content && (
          <div style={{ 
            marginTop: '0.5rem', 
            padding: '0.5rem', 
            background: 'rgba(0, 0, 0, 0.05)', 
            borderRadius: '4px',
            fontSize: '0.875rem'
          }}>
            {message.content}
          </div>
        )}
      </ImageMessageContainer>
      
      {isFullscreen && (
        <ImageFullscreen onClick={handleCloseFullscreen}>
          <ImageOverlay>
            <img
              src={imageUrl}
              alt={imageName}
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
            <div style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '0.5rem',
              borderRadius: '4px',
              cursor: 'pointer'
            }} onClick={handleCloseFullscreen}>
              ✕
            </div>
          </ImageOverlay>
        </ImageFullscreen>
      )}
    </>
  );
};
